/**
 * Control-bar child component ordering for host vs viewer roles.
 *
 * Video.js builds the control bar from an ordered list of registered
 * component names. These arrays are passed as `controlBar.children` in the
 * player options (see {@link useVideoJs}). Custom components registered by
 * {@link registerComponents} are referenced here by their registered name.
 *
 * Order is significant: it is the left-to-right render order of the bar.
 */

/**
 * Ordered control-bar children for the host (full transport controls).
 *
 * Includes play/pause, skip, and the scrub bar (`progressControl`) so the
 * host can seek. The two custom controls (`SettingsMenuButton`,
 * `SyncStatusIndicator`) are owned by WS-7. `SettingsMenuButton` is the one
 * gear for subtitles, audio track, quality and both delay sliders.
 */
export const HOST_CONTROL_BAR: readonly string[] = [
  'playToggle',
  'skipBackward',
  'skipForward',
  'volumePanel',
  'currentTimeDisplay',
  'timeDivider',
  'durationDisplay',
  'progressControl',
  'remainingTimeDisplay',
  'customControlSpacer',
  'SyncStatusIndicator',
  'SettingsMenuButton',
  'pictureInPictureToggle',
  'fullscreenToggle',
];

/**
 * Ordered control-bar children for viewers.
 *
 * Same as {@link HOST_CONTROL_BAR} minus `playToggle`, `skipBackward`,
 * `skipForward`, and `progressControl`, so a viewer cannot play/pause or
 * seek. The time displays (`currentTimeDisplay` / `timeDivider` /
 * `durationDisplay` / `remainingTimeDisplay`) remain as a non-interactive
 * progress readout so viewers still see position and duration.
 *
 * WHY: Video.js core has no arrow-key seeking unless a hotkeys plugin is
 * added, and we set `userActions.hotkeys: false`, so removing the scrub
 * components is sufficient to prevent viewer transport control — no extra
 * CSS hiding or `disable()` calls are required.
 */
export const VIEWER_CONTROL_BAR: readonly string[] = [
  'volumePanel',
  'currentTimeDisplay',
  'timeDivider',
  'durationDisplay',
  'remainingTimeDisplay',
  'customControlSpacer',
  'SyncStatusIndicator',
  'SettingsMenuButton',
  'pictureInPictureToggle',
  'fullscreenToggle',
];
